import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LocaleProvider, useLocale } from "@/i18n/LocaleContext";
import { isLang } from "@/i18n";
import { initLenis, scrollToTop } from "@/lib/scroll";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import Treatments from "@/pages/Treatments";
import TreatmentDetail from "@/pages/TreatmentDetail";
import Category from "@/pages/Category";
import About from "@/pages/About";
import Studio from "@/pages/Studio";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import BioLink from "@/components/BioLink";

function RootRedirect() {
  const saved = localStorage.getItem("imperium-lang");
  return <Navigate to={`/${isLang(saved) ? saved : "de"}`} replace />;
}

function SiteInner() {
  const { lang, pageKey, treatment, category, t, langValid } = useLocale();

  useEffect(() => {
    document.documentElement.lang = lang === "de" ? "de-CH" : lang;
    const brand = "IMPERIUM Beauty & Wellness";
    let title = t("seo.homeTitle");
    let desc = t("seo.homeDesc");
    if (pageKey === "treatments") title = `${t("treatmentsPage.title")} | ${brand}`;
    else if (pageKey === "treatment" && treatment) title = `${treatment.names[lang]} | ${brand}`;
    else if (pageKey === "category" && category) title = `${category.names[lang]} | ${brand}`;
    else if (pageKey === "about") title = `${t("about.title")} — ${t("about.eyebrow")} | ${brand}`;
    else if (pageKey === "studio") title = `${t("studio.title")} | ${brand}`;
    else if (pageKey === "contact") title = `${t("contact.eyebrow")} | ${brand}`;
    else if (pageKey === "impressum") title = `${t("impressum.title")} | ${brand}`;
    else if (pageKey === "privacy") title = `${t("privacyPage.title")} | ${brand}`;
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", desc);
  }, [lang, pageKey, treatment, category, t]);

  let page;
  if (!langValid || pageKey === "notfound") page = <NotFound />;
  else if (pageKey === "home") page = <Home />;
  else if (pageKey === "treatments") page = <Treatments />;
  else if (pageKey === "treatment") page = <TreatmentDetail />;
  else if (pageKey === "category") page = <Category />;
  else if (pageKey === "about") page = <About />;
  else if (pageKey === "studio") page = <Studio />;
  else if (pageKey === "contact") page = <Contact />;
  else if (pageKey === "impressum") page = <Legal type="impressum" />;
  else if (pageKey === "privacy") page = <Legal type="privacy" />;
  else page = <NotFound />;

  return <Layout>{page}</Layout>;
}

function Site() {
  const location = useLocation();
  useEffect(() => {
    scrollToTop();
  }, [location.pathname]);
  return (
    <LocaleProvider>
      <SiteInner />
    </LocaleProvider>
  );
}

function App() {
  useEffect(() => {
    initLenis();
  }, []);
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/links" element={<BioLink />} />
        <Route path="/bio" element={<BioLink />} />
        <Route path="/:lang/*" element={<Site />} />
        <Route path="*" element={<Navigate to="/de" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
